<template>
    <dialog v-if="isRendered" ref="dialog" class="e-mobile-modal"
        :class="[positionClass, dialogClass, { 'is-closing': isClosing, 'e-mobile-modal-fullscreen': fullscreen }]"
        :open="fallbackOpen" :aria-label="ariaLabel" @close="onDialogClose" @cancel="onDialogCancel"
        @click="onBackdropClick" @focusin="onDialogFocusIn">
        <div class="e-mobile-modal__panel" :class="panelClass">
            <div v-if="showHeader" class="e-mobile-modal__header" :class="{ 'header-no-padding': headerNoPadding }">
                <slot name="header" :close="closeDialog">
                    <div class="e-mobile-modal__titles" :class="{ 'flex-reverse'  : descriptionFirst }">
                        <div v-if="hasTitle" class="e-mobile-modal__title">
                            <slot name="title">{{ title }}</slot>
                        </div>
                        <div v-if="hasDescription" class="e-mobile-modal__description">
                            <slot name="description">{{ description }}</slot>
                        </div>
                    </div>
                </slot>
                <button v-if="!hideClose" type="button" class="e-mobile-modal__close" @click="closeDialog">
                    <slot name="close">
                        <i class="fas fa-times"></i>
                    </slot>
                </button>
            </div>
            <slot name="above-body"></slot>
            <div class="e-mobile-modal__body" :class="{ 'body-no-padding': bodyNoPadding }">
                <slot />
            </div>
            <div v-if="hasFooterSlot" class="e-mobile-modal__footer">
                <slot name="footer" :close="closeDialog" />
            </div>
        </div>
    </dialog>
</template>

<script>
/**
 * @typedef {Object} EMobileModalProps
 * @property {boolean} value Controls modal visibility with `v-model`.
 * @property {string} title Header title text when the `title` slot is not used.
 * @property {string} description Header description text when the `description` slot is not used.
 * @property {`center`|`bottom`|`top`} position Modal placement on screen.
 * @property {boolean} closeOnBackdrop Whether clicking the backdrop closes the modal.
 * @property {boolean} closeOnEscape Whether pressing `Escape` closes the modal.
 * @property {boolean} hideClose Whether to render the close button in the header.
 * @property {string|Array|Object} dialogClass Extra class(es) applied to the `<dialog>` element.
 * @property {string|Array|Object} panelClass Extra class(es) applied to the panel wrapper.
 * @property {string} ariaLabel Accessible label for the dialog element.
 */
export default {
    name: `e-mobile-modal`,
    /** @type {EMobileModalProps} */
    props: {
        value: { type: Boolean, default: false },
        title: { type: String, default: `` },
        description: { type: String, default: `` },
        descriptionFirst: { type: Boolean, default: false },
        position: { type: String, default: null },
        closeOnBackdrop: { type: Boolean, default: true },
        closeOnEscape: { type: Boolean, default: true },
        hideClose: { type: Boolean, default: false },
        dialogClass: { type: [String, Array, Object], default: `` },
        panelClass: { type: [String, Array, Object], default: `` },
        ariaLabel: { type: String, default: `Dialog` },
        headerNoPadding: { type: Boolean, default: false },
        bodyNoPadding: { type: Boolean, default: false },
        fullscreen: { type: Boolean, default: false }
    },
    emits: [`input`, `opened`, `closed`],
    computed: {
        isRendered() {
            return this.value || this.fallbackOpen || this.isClosing;
        },
        hasTitle() {
            return Boolean(this.title || this.$slots.title);
        },
        hasDescription() {
            return Boolean(this.description || this.$slots.description);
        },
        hasFooterSlot() {
            return Boolean(this.$slots.footer || this.$scopedSlots.footer);
        },
        showHeader() {
            return Boolean(this.$slots.header || this.hasTitle || this.hasDescription || !this.hideClose);
        },
        positionClass() {
            const allowed = new Set([`center`, `bottom`, `top`]);
            const defaultPosition = this.isDesktopDevice || this.isKioskDevice ? `center` : `bottom`;
            const value = allowed.has(this.position) ? this.position : defaultPosition;
            return `e-mobile-modal--${value}`;
        }
    },
    watch: {
        value: {
            immediate: true,
            handler(value) {
                if (value) {
                    this.cancelCloseAnimation();
                    this.$nextTick(() => this.openDialog());
                } else {
                    this.closeDialogWithoutUpdate();
                }
            }
        }
    },
    data() {
        return {
            fallbackOpen: false,
            isClosing: false,
            closeTimer: null,
            closeCallbacks: [],
            viewportRaf: null
        };
    },
    mounted() {
        this.bindViewportListeners();
        if (this.value) {
            this.$nextTick(() => this.openDialog());
        }
    },
    beforeDestroy() {
        this.unbindViewportListeners();
        this.closeDialogWithoutUpdate(true);
    },
    beforeUnmount() {
        this.unbindViewportListeners();
        this.closeDialogWithoutUpdate(true);
    },
    methods: {
        openDialog() {
            const dialog = this.$refs.dialog;
            if (!dialog) return;
            this.cancelCloseAnimation();
            if (dialog.open) return;
            if (typeof dialog.showModal === `function`) {
                try {
                    dialog.showModal();
                    this.fallbackOpen = false;
                } catch (error) {
                    this.fallbackOpen = true;
                }
            } else {
                this.fallbackOpen = true;
            }
            this.scheduleViewportUpdate();
            this.$emit(`opened`);
        },
        closeDialog(afterClose = null) {
            this.queueCloseCallback(afterClose);
            this.startCloseDialog(true);
        },
        closeDialogWithoutUpdate(immediate = false) {
            this.startCloseDialog(false, immediate);
        },
        startCloseDialog(emitUpdate = true, immediate = false) {
            const shouldEmitUpdate = emitUpdate;
            const shouldCloseImmediately = immediate;

            const dialog = this.$refs.dialog;
            if (shouldCloseImmediately) {
                this.finishCloseDialog(shouldEmitUpdate);
                return;
            }

            if (this.isClosing) return;

            if (!(dialog && dialog.open) && !this.fallbackOpen) {
                if (shouldEmitUpdate && this.value) {
                    this.$emit(`input`, false);
                }
                this.runCloseCallbacks();
                return;
            }

            this.isClosing = true;
            this.clearCloseTimer();

            this.closeTimer = setTimeout(() => {
                this.finishCloseDialog(shouldEmitUpdate);
            }, this.getCloseAnimationDuration());
        },
        finishCloseDialog(emitUpdate = true) {
            const dialog = this.$refs.dialog;
            this.clearCloseTimer();

            if (dialog && dialog.open) {
                dialog.close();
            }

            if (this.fallbackOpen) {
                this.fallbackOpen = false;
            }

            this.isClosing = false;

            if (emitUpdate && this.value) {
                this.$emit(`input`, false);
            }

            this.runCloseCallbacks();
        },
        cancelCloseAnimation() {
            this.clearCloseTimer();
            this.isClosing = false;
            this.clearCloseCallbacks();
        },
        clearCloseTimer() {
            if (!this.closeTimer) return;
            clearTimeout(this.closeTimer);
            this.closeTimer = null;
        },
        queueCloseCallback(callback) {
            if (typeof callback !== `function`) return;
            this.closeCallbacks.push(callback);
        },
        runCloseCallbacks() {
            if (!this.closeCallbacks.length) return;

            const callbacks = [...this.closeCallbacks];
            this.closeCallbacks = [];

            for (const callback of callbacks) {
                callback();
            }
        },
        clearCloseCallbacks() {
            this.closeCallbacks = [];
        },
        bindViewportListeners() {
            if (typeof window === `undefined`) return;
            window.addEventListener(`resize`, this.scheduleViewportUpdate, { passive: true });
            if (window.visualViewport) {
                window.visualViewport.addEventListener(`resize`, this.scheduleViewportUpdate, { passive: true });
                window.visualViewport.addEventListener(`scroll`, this.scheduleViewportUpdate, { passive: true });
            }
            this.scheduleViewportUpdate();
        },
        unbindViewportListeners() {
            if (typeof window === `undefined`) return;
            window.removeEventListener(`resize`, this.scheduleViewportUpdate);
            if (window.visualViewport) {
                window.visualViewport.removeEventListener(`resize`, this.scheduleViewportUpdate);
                window.visualViewport.removeEventListener(`scroll`, this.scheduleViewportUpdate);
            }
            if (this.viewportRaf) {
                window.cancelAnimationFrame(this.viewportRaf);
                this.viewportRaf = null;
            }
        },
        scheduleViewportUpdate() {
            if (typeof window === `undefined`) return;
            if (this.viewportRaf) {
                window.cancelAnimationFrame(this.viewportRaf);
            }
            this.viewportRaf = window.requestAnimationFrame(() => {
                this.viewportRaf = null;
                this.updateViewportMetrics();
            });
        },
        updateViewportMetrics() {
            const dialog = this.$refs.dialog;
            if (!dialog) return;

            const viewport = window.visualViewport;
            const viewportHeight = viewport ? viewport.height : window.innerHeight;
            const viewportTop = viewport ? viewport.offsetTop : 0;
            const visibleBottom = viewport ? viewport.height + viewport.offsetTop : window.innerHeight;
            const keyboardOffset = Math.max(0, window.innerHeight - visibleBottom);

            dialog.style.setProperty(`--e-mobile-modal-viewport-height`, `${viewportHeight}px`);
            dialog.style.setProperty(`--e-mobile-modal-viewport-top`, `${viewportTop}px`);
            dialog.style.setProperty(`--e-mobile-modal-keyboard-offset`, `${keyboardOffset}px`);
        },
        onDialogFocusIn(event) {
            const target = event?.target;
            if (!this.isFocusableField(target)) return;

            this.scheduleViewportUpdate();
            window.setTimeout(() => {
                this.ensureFieldVisible(target);
            }, 260);
        },
        isFocusableField(element) {
            if (!(element instanceof HTMLElement)) return false;
            return Boolean(element.closest(`input, textarea, select, [contenteditable=""], [contenteditable="true"]`));
        },
        ensureFieldVisible(element) {
            if (!(element instanceof HTMLElement) || !element.isConnected) return;
            const body = this.$el?.querySelector(`.e-mobile-modal__body`);
            if (!(body instanceof HTMLElement) || !body.contains(element)) return;

            const bodyRect = body.getBoundingClientRect();
            const elementRect = element.getBoundingClientRect();
            const safePadding = 16;
            const maxVisibleBottom = bodyRect.bottom - safePadding;
            const minVisibleTop = bodyRect.top + safePadding;

            if (elementRect.bottom > maxVisibleBottom) {
                body.scrollTop += elementRect.bottom - maxVisibleBottom;
            } else if (elementRect.top < minVisibleTop) {
                body.scrollTop -= minVisibleTop - elementRect.top;
            }
        },
        getCloseAnimationDuration() {
            const dialog = this.$refs.dialog;
            if (!dialog) return 180;

            const duration = window.getComputedStyle(dialog).getPropertyValue(`--e-mobile-modal-exit-duration`).trim();
            return this.parseDurationMs(duration) || 180;
        },
        parseDurationMs(value) {
            if (!value) return 0;
            if (value.endsWith(`ms`)) {
                return Number.parseFloat(value) || 0;
            }
            if (value.endsWith(`s`)) {
                return (Number.parseFloat(value) || 0) * 1000;
            }
            return Number.parseFloat(value) || 0;
        },
        onDialogClose() {
            this.cancelCloseAnimation();
            if (this.value) {
                this.$emit(`input`, false);
            }
            this.$emit(`closed`);
        },
        onDialogCancel(event) {
            if (!this.closeOnEscape) {
                event.preventDefault();
                return;
            }
            this.closeDialog();
        },
        onBackdropClick(event) {
            if (!this.closeOnBackdrop) return;
            if (event.target === this.$refs.dialog) {
                this.closeDialog();
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.e-mobile-modal {
    --e-mobile-modal-enter-y: 18px;
    --e-mobile-modal-enter-duration: 240ms;
    --e-mobile-modal-exit-duration: 180ms;
    --e-mobile-modal-enter-ease: cubic-bezier(0.22, 1, 0.36, 1);
    --e-mobile-modal-exit-ease: cubic-bezier(0.4, 0, 1, 1);
    --e-mobile-modal-image-enter-duration: 220ms;
    --e-mobile-modal-image-enter-delay: 90ms;
    --e-mobile-modal-image-enter-ease: cubic-bezier(0.22, 1, 0.36, 1);
    --e-mobile-modal-backdrop-end: 0.6;
    --e-mobile-modal-keyboard-offset: 0px;
    --e-mobile-modal-viewport-top: 0px;
    --e-mobile-modal-viewport-height: 100dvh;
    border: none;
    padding: 0;
    margin: 0;
    inset: auto;
    background: transparent;
    width: min(540px, calc(100% - 32px));
    color: inherit;
    text-align: left;
    overflow: visible;
    pointer-events: auto;
}

.e-mobile-modal.e-mobile-modal-fullscreen {
    width: 100dvw;
    max-width: 100dvw;
    height: 100dvh;
    max-height: 100dvh;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    transform: none;

    .e-mobile-modal__header {
        padding: 12px !important;
    }

    .e-mobile-modal__panel {
        border-radius: 0;
        max-height: 100%;
    }

    .e-mobile-modal__body {
        overscroll-behavior: none;
    }
}

.e-mobile-modal::backdrop {
    background: rgba(0, 0, 0, var(--e-mobile-modal-backdrop-end));
    opacity: 0;
    pointer-events: auto;
}

.e-mobile-modal--center {
    --e-mobile-modal-enter-y: 18px;
    position: fixed;
    left: 50%;
    right: auto;
    top: 50%;
    bottom: auto;
    transform: translate(-50%, -50%);
}

.e-mobile-modal--center .e-mobile-modal__panel {
    transform-origin: center center;
}

.e-mobile-modal--bottom {
    --e-mobile-modal-enter-y: 18px;
    --e-mobile-modal-enter-duration: 280ms;
    --e-mobile-modal-enter-ease: cubic-bezier(0.32, 0.72, 0, 1);
    position: fixed;
    left: 50%;
    right: auto;
    top: auto;
    bottom: calc(16px + var(--e-mobile-modal-keyboard-offset));
    transform: translateX(-50%);
    width: min(720px, calc(100% - 24px));
}

.e-mobile-modal--bottom .e-mobile-modal__panel {
    transform-origin: center bottom;
}

.e-mobile-modal--top {
    --e-mobile-modal-enter-y: -18px;
    position: fixed;
    left: 50%;
    right: auto;
    top: 16px;
    bottom: auto;
    transform: translateX(-50%);
    width: min(720px, calc(100% - 24px));
}

.e-mobile-modal--top .e-mobile-modal__panel {
    transform-origin: center top;
}

.e-mobile-modal__panel {
    background: #ffffff;
    border-radius: 16px;
    box-shadow: 0 24px 60px rgba(0, 0, 0, 0.25);
    overflow: hidden;
    will-change: transform, opacity;

    display: flex;
    flex-direction: column;
    max-height: calc(var(--e-mobile-modal-viewport-height) - 32px);
}

.e-mobile-modal[open] .e-mobile-modal__panel {
    animation: e-mobile-modal-panel-in var(--e-mobile-modal-enter-duration) var(--e-mobile-modal-enter-ease) both;
}

.e-mobile-modal[open]::backdrop {
    animation: e-mobile-modal-backdrop-in 180ms ease-out both;
}

.e-mobile-modal[open].is-closing .e-mobile-modal__panel {
    animation: e-mobile-modal-panel-out var(--e-mobile-modal-exit-duration) var(--e-mobile-modal-exit-ease) both;
}

.e-mobile-modal[open].is-closing::backdrop {
    animation: e-mobile-modal-backdrop-out 140ms linear both;
}

.e-mobile-modal__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 16px 20px 0;

    &.header-no-padding {
        padding: 0;
    }
}

.e-mobile-modal__titles {
    display: flex;
    flex-direction: column;
    gap: 4px;
    text-align: left;

    &.flex-reverse {
        flex-direction: column-reverse;
    }
}

.e-mobile-modal__title {
    font-size: 15pt;
    font-weight: 600;
}

.e-mobile-modal__description {
    font-size: 11pt;
    color: #6c757d;
}

.e-mobile-modal__close {
    border: none;
    background: #f2f3f5;
    color: #333;
    width: 32px;
    height: 32px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}

.e-mobile-modal__body {
    position: relative;

    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    overflow-y: auto;
    min-height: 0;

    padding: 16px 16px 16px;

    -webkit-overflow-scrolling: touch;

    &.body-no-padding {
        padding: 0;
    }
}

.e-mobile-modal__footer {
    display: flex;
    gap: 8px;
    padding: 4px 14px 14px;
    flex-shrink: 0;

    .btn {
        border-radius: 8px;
        transition: transform 150ms ease-in-out;

        &:active {
            transform: scale(.9);
        }
    }

    .btn-block + .btn-block {
        margin-top: 0 !important;
    }
}

.e-mobile-modal--bottom .e-mobile-modal__footer {
    flex-direction: column;

    .btn {
        padding-block: 12px;
        font-size: 12pt;
        min-height: 100%;
        // font-weight: 600;
    }
}

@keyframes e-mobile-modal-panel-in {
    0% {
        opacity: 0;
        transform: translateY(var(--e-mobile-modal-enter-y)) scale(0.98);
    }

    100% {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

@keyframes e-mobile-modal-panel-out {
    0% {
        opacity: 1;
        transform: translateY(0) scale(1);
    }

    100% {
        opacity: 0;
        transform: translateY(calc(var(--e-mobile-modal-enter-y) * 0.72)) scale(0.99);
    }
}

@keyframes e-mobile-modal-backdrop-in {
    0% {
        opacity: 0;
    }

    100% {
        opacity: 1;
    }
}

@keyframes e-mobile-modal-backdrop-out {
    0% {
        opacity: 1;
    }

    100% {
        opacity: 0;
    }
}

.e-mobile-modal[open] img {
    opacity: 0;
    transform: translateY(8px) scale(0.985);
    will-change: transform, opacity;
    animation: e-mobile-modal-image-in var(--e-mobile-modal-image-enter-duration) var(--e-mobile-modal-image-enter-ease) both;
    animation-delay: var(--e-mobile-modal-image-enter-delay);
}

@keyframes e-mobile-modal-panel-fade-in {
    0% {
        opacity: 0;
    }

    100% {
        opacity: 1;
    }
}

@keyframes e-mobile-modal-panel-fade-out {
    0% {
        opacity: 1;
    }

    100% {
        opacity: 0;
    }
}

@keyframes e-mobile-modal-image-in {
    0% {
        opacity: 0;
        transform: translateY(8px) scale(0.985);
    }

    100% {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

@media (prefers-reduced-motion: reduce) {
    .e-mobile-modal {
        --e-mobile-modal-enter-y: 0px;
        --e-mobile-modal-enter-duration: 140ms;
        --e-mobile-modal-exit-duration: 120ms;
        --e-mobile-modal-image-enter-duration: 140ms;
        --e-mobile-modal-image-enter-delay: 0ms;
    }

    .e-mobile-modal__panel {
        will-change: opacity;
    }

    .e-mobile-modal[open] .e-mobile-modal__panel {
        animation-name: e-mobile-modal-panel-fade-in;
    }

    .e-mobile-modal[open].is-closing .e-mobile-modal__panel {
        animation-name: e-mobile-modal-panel-fade-out;
    }

    .e-mobile-modal[open]::backdrop {
        animation-duration: 120ms;
    }

    .e-mobile-modal[open].is-closing::backdrop {
        animation-duration: 100ms;
    }

    .e-mobile-modal[open] img {
        transform: none;
    }
}
</style>
